import React, { useState } from 'react';
import { FiPlus, FiTrash2, FiPlay } from 'react-icons/fi';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const methodColors = { GET:'#1d4ed8', POST:'#15803d', PUT:'#a16207', PATCH:'#7e22ce', DELETE:'#dc2626' };
const inputStyle = { width:'100%', padding:'6px 8px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:12, color:'#0f172a', outline:'none', boxSizing:'border-box' };
const rowStyle   = { flex:1, padding:'6px 8px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:11, color:'#0f172a', outline:'none' };
const labelStyle = { fontSize:11, fontWeight:700, color:'#475569', display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' };
const sectionStyle = { display:'flex', flexDirection:'column', gap:6 };

export default function APIEditor({ value = {}, onChange }) {
  const api              = { method:'GET', url:'', headers:[], queryParams:[], authentication:{ type:'none' }, body:'', responseMapping:[], ...value };
  const retryCount       = value.retryCount       ?? 1;
  const happyPathMessage = value.happyPathMessage  || '';
  const failoverMessage  = value.failoverMessage   || '';
  const auth             = api.authentication      || { type:'none' };

  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState(null);

  const upd     = (updates)     => onChange({ ...value, ...updates });
  const updAuth = (authUpdates) => onChange({ ...value, authentication: { ...auth, ...authUpdates } });
  const updRoot = (key, val)    => onChange({ ...value, [key]: val });

  const runTest = async () => {
    if (!api.url) return;
    setTesting(true); setTestResult(null);
    try {
      const headers = Object.fromEntries((api.headers||[]).map(h=>[h.key,h.value]).filter(([k])=>k));
      if (auth.type === 'bearer' && auth.token) headers['Authorization'] = `Bearer ${auth.token}`;
      if (auth.type === 'api_key' && auth.key && auth.value) headers[auth.key] = auth.value;
      if (auth.type === 'basic' && auth.username) headers['Authorization'] = `Basic ${btoa(`${auth.username}:${auth.password||''}`)}`;
      const opts = { method: api.method, headers };
      if (['POST','PUT','PATCH'].includes(api.method) && api.body) {
        opts.body = api.body;
        if (!headers['Content-Type']) headers['Content-Type'] = 'application/json';
      }
      const res  = await fetch(api.url, opts);
      const text = await res.text();
      let parsed; try { parsed = JSON.parse(text); } catch { parsed = text; }
      setTestResult({ ok: res.ok, status: res.status, data: parsed });
    } catch(e) {
      setTestResult({ ok:false, status:0, data: e.message });
    } finally { setTesting(false); }
  };

  return (
    <div style={{ padding:16, display:'flex', flexDirection:'column', gap:16 }}>

      {/* Method + URL */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Request</label>
        <div style={{ display:'flex', gap:6 }}>
          <select value={api.method} onChange={e => upd({ method: e.target.value })}
            style={{ width:90, padding:'7px 8px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:12, fontWeight:700, color: methodColors[api.method]||'#475569', outline:'none' }}>
            {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <input value={api.url || ''} onChange={e => upd({ url: e.target.value })} onKeyDown={e => e.stopPropagation()}
            placeholder="https://api.example.com/endpoint"
            style={{ flex:1, padding:'7px 8px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:11, fontFamily:'monospace', color:'#0f172a', outline:'none' }}/>
        </div>
      </div>

      {/* Authentication */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Authentication</label>
        <select value={auth.type||'none'} onChange={e => updAuth({ type: e.target.value })} style={inputStyle}>
          <option value="none">None</option>
          <option value="bearer">Bearer Token</option>
          <option value="api_key">API Key (Header)</option>
          <option value="basic">Basic Auth</option>
        </select>
        {auth.type === 'bearer' && (
          <input value={auth.token||''} onChange={e => updAuth({ token: e.target.value })} onKeyDown={e => e.stopPropagation()}
            placeholder="Bearer token..." style={{ ...inputStyle, fontFamily:'monospace', fontSize:11 }}/>
        )}
        {auth.type === 'api_key' && (
          <div style={{ display:'flex', gap:6 }}>
            <input value={auth.key||''} onChange={e => updAuth({ key: e.target.value })} onKeyDown={e => e.stopPropagation()}
              placeholder="Header name" style={{ flex:1, ...rowStyle }}/>
            <input value={auth.value||''} onChange={e => updAuth({ value: e.target.value })} onKeyDown={e => e.stopPropagation()}
              placeholder="Value" style={{ flex:1, ...rowStyle }}/>
          </div>
        )}
        {auth.type === 'basic' && (
          <div style={{ display:'flex', gap:6 }}>
            <input value={auth.username||''} onChange={e => updAuth({ username: e.target.value })} onKeyDown={e => e.stopPropagation()}
              placeholder="Username" style={{ flex:1, ...rowStyle }}/>
            <input type="password" value={auth.password||''} onChange={e => updAuth({ password: e.target.value })} onKeyDown={e => e.stopPropagation()}
              placeholder="Password" style={{ flex:1, ...rowStyle }}/>
          </div>
        )}
      </div>

      {/* Headers */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Headers</label>
        {(api.headers||[]).map((h,i) => (
          <div key={i} style={{ display:'flex', gap:4, alignItems:'center' }}>
            <input value={h.key||''} onChange={e => { const a=[...api.headers]; a[i]={...a[i],key:e.target.value}; upd({headers:a}); }} onKeyDown={e=>e.stopPropagation()} placeholder="Key" style={rowStyle}/>
            <input value={h.value||''} onChange={e => { const a=[...api.headers]; a[i]={...a[i],value:e.target.value}; upd({headers:a}); }} onKeyDown={e=>e.stopPropagation()} placeholder="Value" style={rowStyle}/>
            <button onClick={() => upd({headers:(api.headers||[]).filter((_,x)=>x!==i)})}
              style={{ padding:'4px 6px', color:'#dc2626', background:'#fef2f2', border:'none', borderRadius:4, cursor:'pointer' }}><FiTrash2 size={11}/></button>
          </div>
        ))}
        <button onClick={() => upd({headers:[...(api.headers||[]),{key:'',value:''}]})}
          style={{ padding:'5px 10px', fontSize:11, color:'#4f46e5', background:'#eef2ff', border:'none', borderRadius:6, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
          <FiPlus size={11}/> Add Header
        </button>
      </div>

      {/* Query Params */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Query Params</label>
        {(api.queryParams||[]).map((p,i) => (
          <div key={i} style={{ display:'flex', gap:4, alignItems:'center' }}>
            <input value={p.key||''} onChange={e => { const a=[...api.queryParams]; a[i]={...a[i],key:e.target.value}; upd({queryParams:a}); }} onKeyDown={e=>e.stopPropagation()} placeholder="Key" style={rowStyle}/>
            <input value={p.value||''} onChange={e => { const a=[...api.queryParams]; a[i]={...a[i],value:e.target.value}; upd({queryParams:a}); }} onKeyDown={e=>e.stopPropagation()} placeholder="Value or {{var}}" style={{ ...rowStyle, fontFamily:'monospace' }}/>
            <button onClick={() => upd({queryParams:(api.queryParams||[]).filter((_,x)=>x!==i)})}
              style={{ padding:'4px 6px', color:'#dc2626', background:'#fef2f2', border:'none', borderRadius:4, cursor:'pointer' }}><FiTrash2 size={11}/></button>
          </div>
        ))}
        <button onClick={() => upd({queryParams:[...(api.queryParams||[]),{key:'',value:''}]})}
          style={{ padding:'5px 10px', fontSize:11, color:'#4f46e5', background:'#eef2ff', border:'none', borderRadius:6, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
          <FiPlus size={11}/> Add Param
        </button>
      </div>

      {/* Request Body */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Request Body</label>
        <p style={{ fontSize:10, color:'#94a3b8' }}>For POST/PUT/PATCH. Use {'{{variable}}'} syntax.</p>
        <textarea value={api.body||''} onChange={e => upd({ body: e.target.value })} onKeyDown={e => e.stopPropagation()}
          placeholder={'{\n  "key": "{{variable}}"\n}'} rows={4}
          style={{ width:'100%', padding:'7px 8px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:11, fontFamily:'monospace', color:'#0f172a', resize:'vertical', outline:'none', boxSizing:'border-box' }}/>
      </div>

      {/* Response Mapping */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Response Mapping</label>
        <p style={{ fontSize:10, color:'#94a3b8' }}>Map API response fields to flow variables.</p>
        {(api.responseMapping||[]).map((r,i) => (
          <div key={i} style={{ display:'flex', gap:4, alignItems:'center' }}>
            <input value={r.apiPath||''} onChange={e => { const m=[...api.responseMapping]; m[i]={...m[i],apiPath:e.target.value}; upd({responseMapping:m}); }} onKeyDown={e=>e.stopPropagation()} placeholder="response.field" style={{ ...rowStyle, fontFamily:'monospace' }}/>
            <span style={{ color:'#94a3b8', fontSize:13 }}>→</span>
            <input value={r.variableName||''} onChange={e => { const m=[...api.responseMapping]; m[i]={...m[i],variableName:e.target.value}; upd({responseMapping:m}); }} onKeyDown={e=>e.stopPropagation()} placeholder="variable_name" style={{ ...rowStyle, fontFamily:'monospace', color:'#4f46e5' }}/>
            <button onClick={() => upd({responseMapping:(api.responseMapping||[]).filter((_,x)=>x!==i)})}
              style={{ padding:'4px 6px', color:'#dc2626', background:'#fef2f2', border:'none', borderRadius:4, cursor:'pointer' }}><FiTrash2 size={11}/></button>
          </div>
        ))}
        <button onClick={() => upd({responseMapping:[...(api.responseMapping||[]),{apiPath:'',variableName:''}]})}
          style={{ padding:'5px 10px', fontSize:11, color:'#4f46e5', background:'#eef2ff', border:'none', borderRadius:6, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
          <FiPlus size={11}/> Add Mapping
        </button>
      </div>

      {/* Retry & Messages */}
      <div style={{ background:'#f8fafc', borderRadius:8, padding:12, border:'1px solid #e2e8f0', display:'flex', flexDirection:'column', gap:10 }}>
        <p style={{ fontSize:11, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.06em' }}>Retry & Messages</p>
        <div>
          <label style={{ fontSize:10, color:'#94a3b8', display:'block', marginBottom:4 }}>Retry Count</label>
          <input type="number" min={0} max={3} value={retryCount} onChange={e => updRoot('retryCount', parseInt(e.target.value)||0)}
            style={{ width:80, padding:'6px 8px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:12, color:'#0f172a', outline:'none' }}/>
        </div>
        <div>
          <label style={{ fontSize:10, fontWeight:700, color:'#059669', display:'block', marginBottom:4 }}>✓ Happy Path Message</label>
          <textarea value={happyPathMessage} onChange={e => updRoot('happyPathMessage', e.target.value)} onKeyDown={e=>e.stopPropagation()}
            placeholder="Success! {{variable}}" rows={2}
            style={{ width:'100%', padding:'7px 8px', border:'1px solid #d1fae5', borderRadius:6, fontSize:11, color:'#0f172a', outline:'none', boxSizing:'border-box', fontFamily:'inherit', background:'#f0fdf4' }}/>
        </div>
        <div>
          <label style={{ fontSize:10, fontWeight:700, color:'#dc2626', display:'block', marginBottom:4 }}>✗ Failover Message</label>
          <textarea value={failoverMessage} onChange={e => updRoot('failoverMessage', e.target.value)} onKeyDown={e=>e.stopPropagation()}
            placeholder="Sorry, something went wrong." rows={2}
            style={{ width:'100%', padding:'7px 8px', border:'1px solid #fecaca', borderRadius:6, fontSize:11, color:'#0f172a', outline:'none', boxSizing:'border-box', fontFamily:'inherit', background:'#fef2f2' }}/>
        </div>
      </div>

      {/* Test API */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Test API</label>
        <button onClick={runTest} disabled={testing || !api.url}
          style={{ padding:'8px 14px', fontSize:12, fontWeight:600, color:'#fff', background: !api.url||testing ? '#94a3b8':'#4f46e5', border:'none', borderRadius:8, cursor: !api.url||testing ? 'not-allowed':'pointer', display:'flex', alignItems:'center', gap:6 }}>
          <FiPlay size={13}/>{testing ? 'Testing...' : 'Run Test'}
        </button>
        {testResult && (
          <div style={{ background: testResult.ok ? '#f0fdf4':'#fef2f2', border:`1px solid ${testResult.ok?'#d1fae5':'#fecaca'}`, borderRadius:6, padding:10 }}>
            <p style={{ fontSize:11, fontWeight:700, color: testResult.ok?'#059669':'#dc2626', marginBottom:6 }}>
              {testResult.status ? `HTTP ${testResult.status}` : 'Network Error'}
            </p>
            <pre style={{ fontSize:10, color:'#475569', overflow:'auto', maxHeight:150, margin:0, fontFamily:'monospace', whiteSpace:'pre-wrap' }}>
              {typeof testResult.data === 'string' ? testResult.data : JSON.stringify(testResult.data, null, 2)}
            </pre>
          </div>
        )}
      </div>

    </div>
  );
}
